import { createFileRoute, Link } from "@tanstack/react-router";
import { Shield } from "lucide-react";
import { Footer } from "@/components/Footer";

export const Route = createFileRoute("/terms")({
  head: () => ({
    meta: [
      { title: "Terms of Service — LendyTrack" },
      {
        name: "description",
        content:
          "Terms of Service for LendyTrack, a consumer loan intelligence product of Oasis Media Group LLC.",
      },
      { property: "og:title", content: "Terms of Service — LendyTrack" },
      {
        property: "og:description",
        content: "Read the Terms of Service for using LendyTrack.",
      },
      { property: "og:url", content: "/terms" },
    ],
    links: [{ rel: "canonical", href: "/terms" }],
  }),
  component: TermsPage,
});

function TermsPage() {
  return (
    <div className="min-h-screen bg-gradient-surface">
      <header className="border-b border-border bg-card/70 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center gap-2">
            <div className="grid h-9 w-9 place-items-center rounded-lg bg-gradient-hero text-primary-foreground shadow-soft">
              <Shield className="h-5 w-5" />
            </div>
            <span className="font-display text-lg font-semibold">LendyTrack</span>
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-6 py-14">
        <h1 className="font-display text-4xl font-semibold tracking-tight">Terms of Service</h1>
        <p className="mt-2 text-sm text-muted-foreground">Last updated: June 22, 2026</p>

        <div className="mt-10 space-y-8 text-sm leading-relaxed text-foreground">
          <p className="text-muted-foreground">
            This page is maintained by LendyTrack to set out the rules and expectations for using our service.
            It is app-owned content and is not an independent legal opinion or certification.
          </p>

          <section>
            <h2 className="font-display text-xl font-semibold">1. Acceptance of Terms</h2>
            <p className="mt-2 text-muted-foreground">
              By accessing or using LendyTrack, you agree to be bound by these Terms of Service and our Privacy Policy.
              If you do not agree, please do not use the service.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold">2. Eligibility</h2>
            <p className="mt-2 text-muted-foreground">
              You must be at least 18 years old and capable of forming a binding contract to use LendyTrack.
              By using the service, you represent that you meet these requirements.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold">3. Description of Service</h2>
            <p className="mt-2 text-muted-foreground">
              LendyTrack provides lender search, loan-offer analysis, financial-health guidance, and AI-powered coaching.
              Outputs are generated from public data and algorithms and are for educational and informational purposes only.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold">4. Accounts and Security</h2>
            <p className="mt-2 text-muted-foreground">
              You are responsible for keeping your account credentials secure and for all activity that occurs under your account.
              Notify us immediately if you suspect unauthorized access.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold">5. Subscriptions and Payments</h2>
            <p className="mt-2 text-muted-foreground">
              Premium plans are billed through our payment processor. Fees are non-refundable except where required by law or covered by our stated money-back policy.
              You may cancel at any time; cancellation takes effect at the end of the current billing period.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold">6. Prohibited Conduct</h2>
            <p className="mt-2 text-muted-foreground">
              You may not use LendyTrack for unlawful purposes, scrape our data, reverse-engineer the platform, impersonate others, or upload malicious content.
              We reserve the right to suspend accounts that violate these rules.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold">7. Disclaimers</h2>
            <p className="mt-2 text-muted-foreground">
              LendyTrack does not provide financial, legal, or investment advice. Scores, analyses, and coach responses are not recommendations to borrow, lend, or invest.
              Always consult a qualified professional before making financial decisions.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold">8. Limitation of Liability</h2>
            <p className="mt-2 text-muted-foreground">
              To the fullest extent permitted by law, Oasis Media Group LLC and LendyTrack are not liable for indirect, incidental, or consequential damages arising from your use of the service.
              Our total liability is limited to the amount you paid in the twelve months preceding the claim, or $100 if no payment was made.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold">9. Governing Law</h2>
            <p className="mt-2 text-muted-foreground">
              These terms are governed by the laws of the State of Delaware, without regard to conflict-of-law principles.
              Disputes will be resolved in the state or federal courts located in Delaware.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold">10. Changes to These Terms</h2>
            <p className="mt-2 text-muted-foreground">
              We may update these Terms from time to time. Continued use of the service after changes means you accept the updated terms.
              Material changes will be communicated by email or through the app when possible.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold">11. Contact Us</h2>
            <p className="mt-2 text-muted-foreground">
              Questions about these Terms should be sent to{" "}
              <a href="mailto:support@lendytrack.com" className="text-primary hover:underline">support@lendytrack.com</a>.
            </p>
          </section>

          <p className="text-xs text-muted-foreground">
            © {new Date().getFullYear()} LendyTrack. All rights reserved. An Oasis Media Group LLC Company.
          </p>
        </div>
      </main>

      <Footer />
    </div>
  );
}
